import type { ITabKeyCaptureModule } from '../types';

/**
 * TabKeyCapture Module
 * Ermöglicht das "Einfangen" der Tab-Navigation innerhalb eines Elements mit data-tabkey-capture Attribut
 * Die Tab-Navigation wird zyklisch: Beim letzten Element springt Tab zum ersten, beim ersten springt Shift+Tab zum letzten
 */

/**
 * Map aller registrierten Tab-Capture-Handler (Key: Element, Value: Event-Listener)
 */
const registeredHandlers = new Map<HTMLElement, (event: KeyboardEvent) => void>();

/**
 * MutationObserver-Instanz zur Überwachung von DOM-Änderungen
 */
let observer: MutationObserver | null = null;

/**
 * Findet alle fokussierbaren Elemente innerhalb eines Containers
 * @param container - Das Container-Element
 * @returns Array aller fokussierbaren Elemente
 */
function getFocusableElements(container: HTMLElement): HTMLElement[] {
    const focusableSelectors = [
        'a[href]',
        'button:not([disabled])',
        'textarea:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    const elements = Array.from(container.querySelectorAll<HTMLElement>(focusableSelectors));
    
    // Filtere versteckte Elemente aus
    return elements.filter(el => {
        const style = window.getComputedStyle(el);
        return style.display !== 'none' && 
               style.visibility !== 'hidden' && 
               el.offsetParent !== null;
    });
}

/**
 * Registriert einen Tab-Capture-Handler für ein Element
 * @param element - Das Element mit data-tabkey-capture Attribut
 */
function registerTabCapture(element: HTMLElement): void {
    // Verhindere doppelte Registrierung
    if (registeredHandlers.has(element)) {
        return;
    }

    const keydownHandler = (event: KeyboardEvent): void => {
        // Nur Tab-Taste behandeln
        if (event.key !== 'Tab') {
            return;
        }

        const focusableElements = getFocusableElements(element);
        
        // Wenn keine fokussierbaren Elemente vorhanden sind, nichts tun
        if (focusableElements.length === 0) {
            return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const activeElement = document.activeElement as HTMLElement;

        // Shift+Tab beim ersten Element -> springe zum letzten
        if (event.shiftKey && activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
        }
        // Tab beim letzten Element -> springe zum ersten
        else if (!event.shiftKey && activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
        }
    };

    element.addEventListener('keydown', keydownHandler);
    registeredHandlers.set(element, keydownHandler);
}

/**
 * Entfernt den Tab-Capture-Handler von einem Element
 * @param element - Das Element, von dem der Handler entfernt werden soll
 */
function unregisterTabCapture(element: HTMLElement): void {
    const handler = registeredHandlers.get(element);
    if (handler) {
        element.removeEventListener('keydown', handler);
        registeredHandlers.delete(element);
    }
}

/**
 * Initialisiert Tab-Capture für alle Elemente mit data-tabkey-capture Attribut
 * und startet einen MutationObserver zur dynamischen Erkennung neuer/entfernter Elemente
 */
function initializeAll(): void {
    // Registriere alle bestehenden Elemente
    const elements = document.querySelectorAll<HTMLElement>('[data-tabkey-capture]');
    elements.forEach(element => registerTabCapture(element));

    // Stoppe vorhandenen Observer falls vorhanden
    if (observer) {
        observer.disconnect();
    }

    // Erstelle neuen MutationObserver
    observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            // Prüfe hinzugefügte Nodes
            mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                    const element = node as HTMLElement;
                    
                    // Prüfe ob das Element selbst das Attribut hat
                    if (element.hasAttribute('data-tabkey-capture')) {
                        registerTabCapture(element);
                    }
                    
                    // Prüfe Child-Elemente
                    if (element.querySelectorAll) {
                        const childElements = element.querySelectorAll<HTMLElement>('[data-tabkey-capture]');
                        childElements.forEach(child => registerTabCapture(child));
                    }
                }
            });

            // Prüfe entfernte Nodes
            mutation.removedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                    const element = node as HTMLElement;
                    
                    // Prüfe ob das Element selbst das Attribut hat
                    if (element.hasAttribute('data-tabkey-capture')) {
                        unregisterTabCapture(element);
                    }
                    
                    // Prüfe Child-Elemente
                    if (element.querySelectorAll) {
                        const childElements = element.querySelectorAll<HTMLElement>('[data-tabkey-capture]');
                        childElements.forEach(child => unregisterTabCapture(child));
                    }
                }
            });

            // Prüfe geänderte Attribute
            if (mutation.type === 'attributes' && mutation.attributeName === 'data-tabkey-capture') {
                const element = mutation.target as HTMLElement;
                if (element.hasAttribute('data-tabkey-capture')) {
                    registerTabCapture(element);
                } else {
                    unregisterTabCapture(element);
                }
            }
        });
    });

    // Starte Observer auf dem gesamten Document
    observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-tabkey-capture']
    });
}

/**
 * Entfernt alle registrierten Tab-Capture-Handler und stoppt den MutationObserver
 */
function disposeAll(): void {
    // Stoppe Observer
    if (observer) {
        observer.disconnect();
        observer = null;
    }

    // Entferne alle Handler
    registeredHandlers.forEach((handler, element) => {
        element.removeEventListener('keydown', handler);
    });
    registeredHandlers.clear();
}

const TabKeyCaptureModule: ITabKeyCaptureModule = {
    registerTabCapture,
    unregisterTabCapture,
    initializeAll,
    disposeAll
};

export default TabKeyCaptureModule;
